import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { ArrowLeft, Upload, Link as LinkIcon, Image, Video, Music, Loader, Trash2, UserPlus, Shield, BarChart3, Flag, Ban, UserCheck, Ban as BanIcon, CalendarOff, Phone, MessageSquare, ChevronDown, ChevronUp, User, Gauge } from "lucide-react";
import { toast } from "sonner";
import { es } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CreatePostForm() {
  const [content, setContent] = useState("");
  const [mediaType, setMediaType] = useState("none");
  const [externalUrl, setExternalUrl] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!content.trim() && !file && !externalUrl) return;
    setSubmitting(true);
    try {
      let mediaUrl = null;
      if (file) {
        const formData = new FormData();
        formData.append("file", file);
        const { data: uploadRes } = await axios.post(`${API}/feed/upload`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
        mediaUrl = uploadRes.url;
      }
      await axios.post(`${API}/feed`, {
        content: content.trim(),
        media_type: file ? mediaType : null,
        media_url: mediaUrl,
        external_url: externalUrl || null,
      }, { withCredentials: true });
      toast.success("Publicacion creada");
      setContent(""); setFile(null); setExternalUrl(""); setMediaType("none");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al publicar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-[#5B3E2E] text-sm">Contenido</Label>
        <Textarea
          data-testid="post-content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Escribe tu publicación..."
          className="mt-1 bg-transparent border-[#5B3E2E]/20 rounded-xl min-h-[100px] text-[#5B3E2E]"
        />
      </div>
      <div>
        <Label className="text-[#5B3E2E] text-sm mb-2 block">Adjuntar</Label>
        <div className="flex gap-2 flex-wrap">
          {[
            { type: "image", icon: Image, label: "Imagen" },
            { type: "video", icon: Video, label: "Video" },
            { type: "audio", icon: Music, label: "Audio" },
          ].map(({ type, icon: Icon, label }) => (
            <button
              key={type}
              data-testid={`media-type-${type}`}
              onClick={() => setMediaType(mediaType === type ? "none" : type)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all ${
                mediaType === type ? "bg-[#E48D57] text-white" : "bg-[#5B3E2E]/5 text-[#5B3E2E]/60 hover:bg-[#5B3E2E]/10"
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
      </div>
      {mediaType !== "none" && (
        <div>
          <Label className="text-[#5B3E2E] text-sm">Archivo</Label>
          <div className="mt-1 border border-dashed border-[#5B3E2E]/20 rounded-xl p-4 text-center">
            <input type="file" data-testid="post-file-upload" accept={mediaType === "image" ? "image/*" : mediaType === "video" ? "video/*" : "audio/*"} onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm text-[#5B3E2E]/60" />
          </div>
        </div>
      )}
      <div>
        <Label className="text-[#5B3E2E] text-sm">Enlace externo (YouTube, Instagram, etc.)</Label>
        <div className="flex items-center gap-2 mt-1">
          <LinkIcon size={16} className="text-[#5B3E2E]/30" />
          <Input data-testid="post-external-url" value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)} placeholder="https://..." className="bg-transparent border-[#5B3E2E]/20 rounded-xl text-[#5B3E2E] text-sm" />
        </div>
      </div>
      <Button data-testid="submit-post-btn" onClick={handleSubmit} disabled={submitting || (!content.trim() && !file && !externalUrl)} className="w-full bg-[#E48D57] hover:bg-[#D07C46] text-white rounded-full font-medium flex items-center justify-center gap-2">
        {submitting ? <Loader size={16} className="animate-spin" /> : <Upload size={16} />}
        {submitting ? "Publicando..." : "Publicar"}
      </Button>
    </div>
  );
}

function CreateEventForm() {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [streamingUrl, setStreamingUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!title || !date || !time) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/events`, { title, description, date, time, image_url: imageUrl || null, streaming_url: streamingUrl || null }, { withCredentials: true });
      toast.success("Evento creado");
      setTitle(""); setDescription(""); setDate(""); setTime(""); setImageUrl(""); setStreamingUrl("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear evento");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-[#5B3E2E] text-sm">Titulo del evento</Label>
        <Input data-testid="event-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 bg-transparent border-[#5B3E2E]/20 rounded-xl text-[#5B3E2E]" placeholder="Titulo..." />
      </div>
      <div>
        <Label className="text-[#5B3E2E] text-sm">Descripcion</Label>
        <Textarea data-testid="event-description" value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1 bg-transparent border-[#5B3E2E]/20 rounded-xl text-[#5B3E2E]" placeholder="Describe el evento..." />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[#5B3E2E] text-sm">Fecha</Label>
          <Input data-testid="event-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 bg-transparent border-[#5B3E2E]/20 rounded-xl text-[#5B3E2E]" />
        </div>
        <div>
          <Label className="text-[#5B3E2E] text-sm">Hora</Label>
          <Input data-testid="event-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1 bg-transparent border-[#5B3E2E]/20 rounded-xl text-[#5B3E2E]" />
        </div>
      </div>
      <div>
        <Label className="text-[#5B3E2E] text-sm">URL de imagen (opcional)</Label>
        <Input data-testid="event-image-url" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} className="mt-1 bg-transparent border-[#5B3E2E]/20 rounded-xl text-[#5B3E2E]" placeholder="https://..." />
      </div>
      <div>
        <Label className="text-[#5B3E2E] text-sm">Enlace de streaming (Zoom, Meet, etc.)</Label>
        <Input data-testid="event-streaming-url" value={streamingUrl} onChange={(e) => setStreamingUrl(e.target.value)} className="mt-1 bg-transparent border-[#5B3E2E]/20 rounded-xl text-[#5B3E2E]" placeholder="https://zoom.us/j/..." />
      </div>
      <Button data-testid="submit-event-btn" onClick={handleSubmit} disabled={submitting || !title || !date || !time} className="w-full bg-[#6B9070] hover:bg-[#5A8060] text-white rounded-full font-medium">
        {submitting ? "Creando..." : "Crear evento"}
      </Button>
    </div>
  );
}

function ManageAdmins() {
  const [admins, setAdmins] = useState([]);
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchAdmins = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/secondary-admins`, { withCredentials: true });
      setAdmins(data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchAdmins(); }, []);

  const handleCreate = async () => {
    if (!email || !username || !password) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/secondary-admins`, { email, username, password }, { withCredentials: true });
      toast.success("Admin secundario creado");
      setEmail(""); setUsername(""); setPassword("");
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear admin");
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/secondary-admins/${id}`, { withCredentials: true });
      toast.success("Admin eliminado");
      fetchAdmins();
    } catch (err) {
      toast.error("Error al eliminar admin");
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <p className="text-xs tracking-[0.15em] uppercase text-[#5B3E2E]/50 font-semibold">Crear admin secundario</p>
        <Input data-testid="admin-username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Nombre de usuario" className="bg-transparent border-[#5B3E2E]/20 rounded-xl text-[#5B3E2E]" />
        <Input data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="bg-transparent border-[#5B3E2E]/20 rounded-xl text-[#5B3E2E]" />
        <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Contraseña" className="bg-transparent border-[#5B3E2E]/20 rounded-xl text-[#5B3E2E]" />
        <Button data-testid="create-admin-btn" onClick={handleCreate} disabled={submitting || !email || !username || !password} className="w-full bg-[#6B9070] hover:bg-[#5A8060] text-white rounded-full font-medium flex items-center justify-center gap-2">
          <UserPlus size={16} />
          {submitting ? "Creando..." : "Crear admin secundario"}
        </Button>
      </div>

      <div>
        <p className="text-xs tracking-[0.15em] uppercase text-[#5B3E2E]/50 font-semibold mb-3">Admins secundarios ({admins.length})</p>
        {loading ? (
          <Loader size={20} className="text-[#E48D57] animate-spin mx-auto" />
        ) : admins.length === 0 ? (
          <p className="text-sm text-[#5B3E2E]/40 text-center py-4">No hay admins secundarios</p>
        ) : (
          <div className="space-y-2">
            {admins.map((a) => (
              <div key={a.id} className="flex items-center justify-between p-3 bg-[#5B3E2E]/5 rounded-xl">
                <div>
                  <p className="text-sm font-medium text-[#5B3E2E]">{a.username}</p>
                  <p className="text-xs text-[#5B3E2E]/50">{a.email}</p>
                </div>
                <button data-testid={`delete-admin-${a.id}`} onClick={() => handleDelete(a.id)} className="text-[#D9534F] hover:text-[#D9534F]/80 p-2">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ManageReportsAndBans() {
  const [reports, setReports] = useState([]);
  const [banned, setBanned] = useState([]);
  const [loadingR, setLoadingR] = useState(true);
  const [loadingB, setLoadingB] = useState(true);

  const fetchReports = async () => { try { const { data } = await axios.get(`${API}/admin/reports`, { withCredentials: true }); setReports(data); } catch {} finally { setLoadingR(false); } };
  const fetchBanned = async () => { try { const { data } = await axios.get(`${API}/admin/banned-users`, { withCredentials: true }); setBanned(data); } catch {} finally { setLoadingB(false); } };

  useEffect(() => { fetchReports(); fetchBanned(); }, []);

  const handleBan = async (userId, username) => {
    if (!window.confirm(`¿Banear permanentemente a ${username}? Se le retirará de todas las salas.`)) return;
    try {
      await axios.post(`${API}/admin/ban/${userId}`, { reason: "Reporte de la comunidad" }, { withCredentials: true });
      toast.success(`${username} ha sido baneado`);
      fetchReports(); fetchBanned();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleUnban = async (userId, username) => {
    try {
      await axios.post(`${API}/admin/unban/${userId}`, {}, { withCredentials: true });
      toast.success(`${username} desbaneado`);
      fetchBanned();
    } catch { toast.error("Error al desbanear"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs tracking-[0.15em] uppercase text-[#5B3E2E]/50 font-semibold mb-3 flex items-center gap-2"><Flag size={13} /> Reportes ({reports.length})</p>
        {loadingR ? <Loader size={18} className="text-[#E48D57] animate-spin mx-auto" /> : reports.length === 0 ? (
          <p className="text-sm text-[#5B3E2E]/40 text-center py-4">No hay reportes pendientes</p>
        ) : (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {reports.map(r => (
              <div key={r.id} className="p-3 bg-[#D9534F]/5 rounded-xl border border-[#D9534F]/10">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-medium text-[#5B3E2E]">{r.reporter_name} reportó a usuario</p>
                  <span className="text-[9px] text-[#5B3E2E]/30">{new Date(r.created_at).toLocaleDateString("es")}</span>
                </div>
                <p className="text-xs text-[#5B3E2E]/60 mb-2">"{r.reason}"</p>
                <Button size="sm" onClick={() => handleBan(r.reported_user_id, "usuario reportado")} className="h-7 text-[10px] bg-[#D9534F] hover:bg-[#c9453f] text-white rounded-full px-3">
                  <Ban size={11} className="mr-1" /> Banear usuario
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <p className="text-xs tracking-[0.15em] uppercase text-[#5B3E2E]/50 font-semibold mb-3 flex items-center gap-2"><Ban size={13} /> Usuarios baneados ({banned.length})</p>
        {loadingB ? <Loader size={18} className="text-[#E48D57] animate-spin mx-auto" /> : banned.length === 0 ? (
          <p className="text-sm text-[#5B3E2E]/40 text-center py-4">No hay usuarios baneados</p>
        ) : (
          <div className="space-y-2">
            {banned.map(u => (
              <div key={u.id} className="flex items-center justify-between p-3 bg-[#5B3E2E]/5 rounded-xl">
                <div>
                  <p className="text-sm font-medium text-[#5B3E2E]">{u.username}</p>
                  <p className="text-[10px] text-[#5B3E2E]/40">{u.email} — {u.reason}</p>
                </div>
                <button onClick={() => handleUnban(u.id, u.username)} className="text-[#6B9070] hover:text-[#6B9070]/80 p-2" title="Desbanear">
                  <UserCheck size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ManageSessionSlots() {
  const [slots, setSlots] = useState([]); // todas las franjas del backend (con reason)
  const [blocked, setBlocked] = useState([]); // listado de bloqueadas crudas (con id/reason)
  const [bookings, setBookings] = useState([]); // todas las sesiones reservadas (admin)
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [submitting, setSubmitting] = useState(false);

  const toIso = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const load = async () => {
    try {
      const [{ data: slotsRes }, { data: blockedRes }, { data: bookingsRes }] = await Promise.all([
        axios.get(`${API}/sessions/slots?days_ahead=90`, { withCredentials: true }),
        axios.get(`${API}/admin/sessions/blocked-slots?days_ahead=90`, { withCredentials: true }),
        axios.get(`${API}/admin/sessions`, { withCredentials: true }),
      ]);
      setSlots(slotsRes.slots || []);
      setBlocked(blockedRes || []);
      setBookings(bookingsRes || []);
    } catch (err) {
      toast.error("No se pudieron cargar las franjas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const slotsByDate = useMemo(() => {
    const m = new Map();
    for (const s of slots) {
      if (!m.has(s.date)) m.set(s.date, []);
      m.get(s.date).push(s);
    }
    return m;
  }, [slots]);

  const blockedDates = useMemo(
    () => blocked.map(b => {
      const [y, mo, d] = b.date.split("-").map(Number);
      return new Date(y, mo - 1, d);
    }),
    [blocked]
  );

  // Días con sesiones reservadas (no canceladas) — para mostrar marca en el calendario
  const bookedDates = useMemo(
    () => Array.from(new Set(bookings.filter(b => b.status !== "cancelled").map(b => b.date)))
      .map(date => {
        const [y, mo, d] = date.split("-").map(Number);
        return new Date(y, mo - 1, d);
      }),
    [bookings]
  );

  // Lookup rápido de booking por fecha+hora
  const bookingsByKey = useMemo(() => {
    const m = new Map();
    for (const b of bookings) {
      if (b.status === "cancelled") continue;
      m.set(`${b.date}::${b.time}`, b);
    }
    return m;
  }, [bookings]);

  const isDateDisabled = (date) => {
    const wd = date.getDay();
    return wd === 0 || wd === 6;
  };

  const selectedIso = selectedDate ? toIso(selectedDate) : null;

  // Filas a mostrar para el día elegido: combina franjas del backend (futuro) con sesiones pasadas
  const dayRows = useMemo(() => {
    if (!selectedIso) return [];
    const slotItems = (slotsByDate.get(selectedIso) || []).map(s => ({ ...s }));
    const slotTimes = new Set(slotItems.map(s => s.time));
    for (const b of bookings) {
      if (b.date === selectedIso && !slotTimes.has(b.time) && b.status !== "cancelled") {
        slotItems.push({ date: b.date, time: b.time, available: false, reason: "booked", past: true });
      }
    }
    return slotItems.sort((a, b) => a.time.localeCompare(b.time));
  }, [selectedIso, slotsByDate, bookings]);

  const [expandedBookingKey, setExpandedBookingKey] = useState(null);

  const handleBlock = async (time) => {
    if (!selectedIso) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/sessions/blocked-slots`, { date: selectedIso, time }, { withCredentials: true });
      toast.success(`Franja ${time} bloqueada`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo bloquear");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnblock = async (time) => {
    if (!selectedIso) return;
    setSubmitting(true);
    try {
      await axios.delete(`${API}/admin/sessions/blocked-slots`, {
        params: { date: selectedIso, time },
        withCredentials: true,
      });
      toast.success(`Franja ${time} desbloqueada`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo desbloquear");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-10"><Loader size={22} className="text-[#E48D57] animate-spin" /></div>;
  }

  return (
    <div data-testid="admin-session-slots" className="space-y-4">
      <div>
        <h3 className="font-heading text-lg text-[#5B3E2E] mb-1 flex items-center gap-2">
          <CalendarOff size={18} className="text-[#E48D57]" />
          Gestión de mis franjas
        </h3>
        <p className="text-xs text-[#5B3E2E]/55 leading-relaxed">
          Bloquea las franjas en las que no estarás disponible (vacaciones, descanso…). Las franjas bloqueadas no podrán ser elegidas por los usuarios.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-[#5B3E2E]/10 p-3">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(d) => d && setSelectedDate(d)}
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          locale={es}
          disabled={isDateDisabled}
          modifiers={{ hasBlocked: blockedDates, hasBooking: bookedDates }}
          modifiersClassNames={{
            hasBlocked: "bg-[#c44a3a]/15 text-[#c44a3a] font-semibold rounded-full",
            hasBooking: "!bg-[#6B9070]/15 !text-[#6B9070] font-semibold rounded-full ring-2 ring-[#6B9070]/30",
          }}
          className="mx-auto [&_.rdp-caption]:px-0 [&_.rdp-cell]:p-0 [&_.rdp-day]:h-9 [&_.rdp-day]:w-9 [&_.rdp-head_cell]:w-9 [&_.rdp-table]:w-full"
          classNames={{
            months: "flex flex-col",
            month: "space-y-2",
            caption: "flex justify-between items-center px-2",
            caption_label: "text-base font-heading font-semibold text-[#5B3E2E] capitalize",
            nav: "flex items-center gap-1",
            nav_button: "h-8 w-8 bg-transparent p-0 text-[#5B3E2E]/60 hover:text-[#5B3E2E] rounded-full hover:bg-[#5B3E2E]/5",
            table: "w-full border-collapse",
            head_row: "flex justify-around",
            head_cell: "text-[#5B3E2E]/40 text-xs font-medium w-9 text-center",
            row: "flex justify-around mt-1",
            cell: "text-center p-0",
            day: "h-9 w-9 text-sm font-medium rounded-full hover:bg-[#5B3E2E]/5 text-[#5B3E2E] transition-colors flex items-center justify-center",
            day_selected: "!bg-[#6B9070] !text-white hover:!bg-[#6B9070] !ring-0",
            day_today: "font-bold text-[#E48D57]",
            day_outside: "text-[#5B3E2E]/20",
            day_disabled: "text-[#5B3E2E]/15",
          }}
        />
        <div className="flex items-center justify-center flex-wrap gap-x-4 gap-y-1 mt-2 text-[10px] text-[#5B3E2E]/55">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#6B9070]/40" /> Con sesiones</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#c44a3a]/40" /> Con franjas bloqueadas</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#6B9070]" /> Día elegido</span>
        </div>
      </div>

      {selectedDate && (
        <div className="bg-white rounded-2xl border border-[#5B3E2E]/10 p-4">
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#5B3E2E]/40 font-semibold mb-3">
            {selectedDate.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
          </p>
          {dayRows.length === 0 ? (
            <p className="text-xs text-[#5B3E2E]/50">Día no laborable o sin sesiones.</p>
          ) : (
            <div className="space-y-2">
              {dayRows.map(s => {
                const isBooked = s.reason === "booked";
                const isBlocked = s.reason === "blocked";
                const bKey = `${s.date}::${s.time}`;
                const booking = bookingsByKey.get(bKey);
                const expanded = expandedBookingKey === bKey;
                return (
                  <div key={s.time} data-testid={`admin-slot-row-${s.date}-${s.time}`} className="rounded-xl bg-[#FDFAF4] border border-[#5B3E2E]/8 overflow-hidden">
                    <div className="flex items-center justify-between gap-3 p-2.5">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-[#5B3E2E] text-sm">{s.time}</span>
                        {isBooked && <span className="text-[10px] bg-[#6B9070]/15 text-[#6B9070] px-2 py-0.5 rounded-full">Reservada</span>}
                        {isBlocked && <span className="text-[10px] bg-[#c44a3a]/15 text-[#c44a3a] px-2 py-0.5 rounded-full">Bloqueada</span>}
                        {!isBooked && !isBlocked && <span className="text-[10px] bg-[#5B3E2E]/8 text-[#5B3E2E]/70 px-2 py-0.5 rounded-full">Libre</span>}
                        {s.past && <span className="text-[10px] bg-[#5B3E2E]/8 text-[#5B3E2E]/50 px-2 py-0.5 rounded-full">Pasada</span>}
                      </div>
                      {isBooked && booking ? (
                        <Button
                          data-testid={`admin-booking-toggle-${s.date}-${s.time}`}
                          size="sm"
                          variant="outline"
                          onClick={() => setExpandedBookingKey(expanded ? null : bKey)}
                          className="h-7 rounded-full text-xs border-[#6B9070]/30 text-[#6B9070] hover:bg-[#6B9070]/5 flex items-center gap-1"
                        >
                          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                          Ver datos
                        </Button>
                      ) : isBlocked ? (
                        <Button
                          data-testid={`admin-unblock-${s.date}-${s.time}`}
                          size="sm"
                          variant="outline"
                          disabled={submitting}
                          onClick={() => handleUnblock(s.time)}
                          className="h-7 rounded-full text-xs border-[#c44a3a]/30 text-[#c44a3a] hover:bg-[#c44a3a]/5"
                        >
                          Desbloquear
                        </Button>
                      ) : !isBooked ? (
                        <Button
                          data-testid={`admin-block-${s.date}-${s.time}`}
                          size="sm"
                          disabled={submitting}
                          onClick={() => handleBlock(s.time)}
                          className="h-7 rounded-full text-xs bg-[#5B3E2E] hover:bg-[#4a3326] text-white flex items-center gap-1"
                        >
                          <BanIcon size={12} /> Bloquear
                        </Button>
                      ) : null}
                    </div>

                    {isBooked && booking && expanded && (
                      <div data-testid={`admin-booking-details-${s.date}-${s.time}`} className="px-3 pb-3 border-t border-[#5B3E2E]/8 pt-3 bg-white">
                        <div className="space-y-2 text-[13px]">
                          <div className="flex items-start gap-2">
                            <User size={13} className="text-[#5B3E2E]/50 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-[10px] text-[#5B3E2E]/45 uppercase tracking-[0.15em]">Usuario</p>
                              <p className="text-[#5B3E2E] font-medium">{booking.username || "—"}</p>
                              <p className="text-[11px] text-[#5B3E2E]/55 break-all">{booking.email}</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <Phone size={13} className="text-[#5B3E2E]/50 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-[10px] text-[#5B3E2E]/45 uppercase tracking-[0.15em]">Teléfono</p>
                              <a href={`tel:${booking.phone}`} className="text-[#E48D57] font-medium underline">{booking.phone}</a>
                              <span className="text-[11px] text-[#5B3E2E]/55 ml-2">
                                · {booking.modality === "video" ? "Llamada de WhatsApp" : "Llamada de teléfono"}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <MessageSquare size={13} className="text-[#5B3E2E]/50 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-[10px] text-[#5B3E2E]/45 uppercase tracking-[0.15em]">Notas del usuario</p>
                              {booking.notes ? (
                                <p className="text-[#5B3E2E]/85 leading-relaxed whitespace-pre-wrap">{booking.notes}</p>
                              ) : (
                                <p className="text-[12px] text-[#5B3E2E]/40 italic">— Sin notas —</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 pt-2 mt-2 border-t border-[#5B3E2E]/8">
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                              booking.status === "confirmed"
                                ? "bg-[#6B9070]/15 text-[#6B9070]"
                                : booking.status === "pending_payment"
                                  ? "bg-[#F0A82C]/15 text-[#a06b00]"
                                  : "bg-[#5B3E2E]/10 text-[#5B3E2E]"
                            }`}>
                              {booking.status === "confirmed" ? "Confirmada (pagada)" : booking.status === "pending_payment" ? "Pendiente de pago" : booking.status}
                            </span>
                            {booking.amount_eur != null && (
                              <span className="text-[11px] text-[#5B3E2E]/55">{Number(booking.amount_eur).toFixed(2).replace(".", ",")} €</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}



export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (user?.role !== "admin" && user?.role !== "secondary_admin") {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <p className="text-[#5B3E2E]/60">No tienes acceso a esta pagina</p>
      </div>
    );
  }

  const isPrimaryAdmin = user?.role === "admin";

  return (
    <div data-testid="admin-page" className="max-w-lg mx-auto px-4 py-6">
      <button data-testid="admin-back-btn" onClick={() => navigate("/app/profile")} className="flex items-center gap-2 text-[#5B3E2E]/60 hover:text-[#5B3E2E] mb-6">
        <ArrowLeft size={18} />
        <span className="text-sm">Volver al perfil</span>
      </button>

      <h1 className="font-heading text-2xl text-[#5B3E2E] mb-4">Panel de Administración</h1>

      <button data-testid="go-dashboard-btn" onClick={() => navigate("/app/admin/dashboard")} className="w-full bg-[#FDFAF4] rounded-2xl border border-[#5B3E2E]/10 p-4 flex items-center gap-3 mb-6 hover:bg-[#F0E7D3] transition-colors">
        <BarChart3 size={20} className="text-[#E48D57]" />
        <div>
          <p className="text-sm font-medium text-[#5B3E2E]">Dashboard de métricas</p>
          <p className="text-xs text-[#5B3E2E]/40">Usuarios, salas, conversión, geografía</p>
        </div>
      </button>

      <button data-testid="go-platform-health-btn" onClick={() => navigate("/app/admin/estado")} className="w-full bg-[#FDFAF4] rounded-2xl border border-[#5B3E2E]/10 p-4 flex items-center gap-3 mb-6 -mt-3 hover:bg-[#F0E7D3] transition-colors">
        <Gauge size={20} className="text-[#95B89A]" />
        <div className="text-left">
          <p className="text-sm font-medium text-[#5B3E2E]">Estado de la plataforma</p>
          <p className="text-xs text-[#5B3E2E]/40">Espacio de archivos, velocidad, memoria y conectados</p>
        </div>
      </button>

      <Tabs defaultValue="post" className="w-full">
        <TabsList className={`w-full bg-[#5B3E2E]/5 rounded-2xl p-1 mb-6 ${isPrimaryAdmin ? 'grid grid-cols-5' : 'grid grid-cols-3'}`}>
          <TabsTrigger data-testid="admin-tab-post" value="post" className="rounded-xl text-[10px] data-[state=active]:bg-[#FDFAF4] data-[state=active]:text-[#5B3E2E]">
            Publicar
          </TabsTrigger>
          <TabsTrigger data-testid="admin-tab-event" value="event" className="rounded-xl text-[10px] data-[state=active]:bg-[#FDFAF4] data-[state=active]:text-[#5B3E2E]">
            Evento
          </TabsTrigger>
          <TabsTrigger data-testid="admin-tab-security" value="security" className="rounded-xl text-[10px] data-[state=active]:bg-[#FDFAF4] data-[state=active]:text-[#5B3E2E]">
            Seguridad
          </TabsTrigger>
          {isPrimaryAdmin && (
            <TabsTrigger data-testid="admin-tab-sessions" value="sessions" className="rounded-xl text-[10px] data-[state=active]:bg-[#FDFAF4] data-[state=active]:text-[#5B3E2E]">
              Sesiones
            </TabsTrigger>
          )}
          {isPrimaryAdmin && (
            <TabsTrigger data-testid="admin-tab-admins" value="admins" className="rounded-xl text-[10px] data-[state=active]:bg-[#FDFAF4] data-[state=active]:text-[#5B3E2E]">
              Admins
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="post">
          <div className="bg-[#FDFAF4] rounded-3xl border border-[#5B3E2E]/10 p-6">
            <CreatePostForm />
          </div>
        </TabsContent>
        <TabsContent value="event">
          <div className="bg-[#FDFAF4] rounded-3xl border border-[#5B3E2E]/10 p-6">
            <CreateEventForm />
          </div>
        </TabsContent>
        <TabsContent value="security">
          <div className="bg-[#FDFAF4] rounded-3xl border border-[#5B3E2E]/10 p-6">
            <ManageReportsAndBans />
          </div>
        </TabsContent>
        {isPrimaryAdmin && (
          <TabsContent value="sessions">
            <div className="bg-[#FDFAF4] rounded-3xl border border-[#5B3E2E]/10 p-6">
              <ManageSessionSlots />
            </div>
          </TabsContent>
        )}
        {isPrimaryAdmin && (
          <TabsContent value="admins">
            <div className="bg-[#FDFAF4] rounded-3xl border border-[#5B3E2E]/10 p-6">
              <ManageAdmins />
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
